'use client';

import { useState, useEffect } from 'react';
import AppImage from '@/components/ui/AppImage';
import Icon from '@/components/ui/AppIcon';

interface HeroSectionProps {
  onDownloadClick: (platform: 'ios' | 'android') => void;
}

const HeroSection = ({ onDownloadClick }: HeroSectionProps) => {
  const [isHydrated, setIsHydrated] = useState(false);

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  if (!isHydrated) {
    return (
      <section className="relative pt-32 pb-20 px-4 lg:px-8 bg-gradient-to-br from-background via-card to-background overflow-hidden">
        <div className="absolute inset-0 opacity-20">
          <div className="absolute top-20 left-10 w-72 h-72 bg-primary rounded-full blur-3xl"></div>
          <div className="absolute bottom-20 right-10 w-96 h-96 bg-secondary rounded-full blur-3xl"></div>
        </div>

        <div className="max-w-7xl mx-auto relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="text-center lg:text-left">
              <div className="inline-flex items-center space-x-2 px-4 py-2 bg-primary/10 border border-primary/20 rounded-full mb-6">
                <Icon name="SparklesIcon" size={20} className="text-primary" />
                <span className="text-sm font-medium text-primary">Rated 4.8/5 on App Stores</span>
              </div>

              <h1 className="text-4xl lg:text-6xl font-heading font-bold bg-gradient-to-r from-primary via-primary to-secondary bg-clip-text text-transparent mb-6 leading-tight">
                Download Signaly & Start Trading Smarter
              </h1>

              <p className="text-lg text-muted-foreground mb-8 max-w-xl mx-auto lg:mx-0">
                Join 50,000+ traders making profit with AI-driven signals. No fuss, no long training. Available on iOS and Android.
              </p>

              <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start mb-8">
                <button className="flex items-center justify-center space-x-3 px-8 py-4 bg-foreground text-background rounded-xl hover:scale-105 transition-all duration-200 shadow-lg">
                  <Icon name="DevicePhoneMobileIcon" size={24} />
                  <div className="text-left">
                    <div className="text-xs opacity-80">Download on the</div>
                    <div className="text-lg font-heading font-semibold">App Store</div>
                  </div>
                </button>

                <button className="flex items-center justify-center space-x-3 px-8 py-4 bg-foreground text-background rounded-xl hover:scale-105 transition-all duration-200 shadow-lg">
                  <Icon name="DevicePhoneMobileIcon" size={24} />
                  <div className="text-left">
                    <div className="text-xs opacity-80">Get it on</div>
                    <div className="text-lg font-heading font-semibold">Google Play</div>
                  </div>
                </button>
              </div>

              <div className="flex items-center justify-center lg:justify-start space-x-6 text-sm text-muted-foreground">
                <div className="flex items-center space-x-2">
                  <Icon name="CheckCircleIcon" size={20} className="text-success" />
                  <span>Free to Download</span>
                </div>
                <div className="flex items-center space-x-2">
                  <Icon name="ShieldCheckIcon" size={20} className="text-success" />
                  <span>Secure & Encrypted</span>
                </div>
              </div>
            </div>

            <div className="relative">
              <div className="relative w-full max-w-md mx-auto">
                <div className="absolute inset-0 bg-primary/20 rounded-3xl blur-2xl"></div>
                <AppImage
                  src="https://img.rocket.new/generatedImages/rocket_gen_img_1378307c2-1767038173433.png"
                  alt="Modern smartphone displaying Signaly trading app interface with live market charts and signal notifications on dark themed screen"
                  className="relative z-10 w-full h-auto rounded-3xl shadow-2xl" />

              </div>
            </div>
          </div>
        </div>
      </section>);

  }

  return (
    <section className="relative pt-32 pb-20 px-4 lg:px-8 bg-gradient-to-br from-background via-card to-background overflow-hidden">
      <div className="absolute inset-0 opacity-20">
        <div className="absolute top-20 left-10 w-72 h-72 bg-primary rounded-full blur-3xl"></div>
        <div className="absolute bottom-20 right-10 w-96 h-96 bg-secondary rounded-full blur-3xl"></div>
      </div>

      <div className="max-w-7xl mx-auto relative z-10">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <div className="text-center lg:text-left">
            <div className="inline-flex items-center space-x-2 px-4 py-2 bg-primary/10 border border-primary/20 rounded-full mb-6">
              <Icon name="SparklesIcon" size={20} className="text-primary" />
              <span className="text-sm font-medium text-primary">Rated 4.8/5 on App Stores</span>
            </div>

            <h1 className="text-4xl lg:text-6xl font-heading font-bold bg-gradient-to-r from-primary via-primary to-secondary bg-clip-text text-transparent mb-6 leading-tight">
              Download Signaly & Start Trading Smarter
            </h1>

            <p className="text-lg text-muted-foreground mb-8 max-w-xl mx-auto lg:mx-0">
              Join 50,000+ traders making profit with AI-driven signals. No fuss, no long training. Available on iOS and Android.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start mb-8">
              <button
                onClick={() => onDownloadClick('ios')}
                className="flex items-center justify-center space-x-3 px-8 py-4 bg-foreground text-background rounded-xl hover:scale-105 transition-all duration-200 shadow-lg">

                <Icon name="DevicePhoneMobileIcon" size={24} />
                <div className="text-left">
                  <div className="text-xs opacity-80">Download on the</div>
                  <div className="text-lg font-heading font-semibold">App Store</div>
                </div>
              </button>

              <button
                onClick={() => onDownloadClick('android')}
                className="flex items-center justify-center space-x-3 px-8 py-4 bg-foreground text-background rounded-xl hover:scale-105 transition-all duration-200 shadow-lg">

                <Icon name="DevicePhoneMobileIcon" size={24} />
                <div className="text-left">
                  <div className="text-xs opacity-80">Get it on</div>
                  <div className="text-lg font-heading font-semibold">Google Play</div>
                </div>
              </button>
            </div>

            <div className="flex items-center justify-center lg:justify-start space-x-6 text-sm text-muted-foreground">
              <div className="flex items-center space-x-2">
                <Icon name="CheckCircleIcon" size={20} className="text-success" />
                <span>Free to Download</span>
              </div>
              <div className="flex items-center space-x-2">
                <Icon name="ShieldCheckIcon" size={20} className="text-success" />
                <span>Secure & Encrypted</span>
              </div>
            </div>
          </div>

          <div className="relative">
            <div className="relative w-full max-w-md mx-auto">
              <div className="absolute inset-0 bg-primary/20 rounded-3xl blur-2xl"></div>
              <AppImage
                src="https://img.rocket.new/generatedImages/rocket_gen_img_1378307c2-1767038173433.png"
                alt="Modern smartphone displaying Signaly trading app interface with live market charts and signal notifications on dark themed screen"
                className="relative z-10 w-full h-auto rounded-3xl shadow-2xl" />

            </div>
          </div>
        </div>
      </div>
    </section>);

};

export default HeroSection;